/**
 * Active region — the one utility that turns the backend `region` block into
 * the app's runtime regional behavior, with US as the default.
 *
 * The logged-in user (`GET /users/:id`) now carries a `region` object that
 * decides measurement units, name order, contact-field rules and the locale
 * matrix. This module is the single place that:
 *
 *   1. reads that block off the users-API payload (any reasonable shape),
 *   2. maps its snake_case fields onto the profile shape the resolver wants,
 *   3. fills every missing field from the built-in profile for the market, and
 *   4. defaults the market itself to "US" when the block is absent or unknown.
 *
 * It then hands the normalized region to `setActiveRegion` in
 * `regionalProfile.js`, after which Add Test Person, Edit Test Person and the
 * bulk-import Review all resolve units and rules through the same active
 * region — none of them branch on locale or market themselves.
 *
 * DEFAULTS
 *   - No block, unknown market, or a market the app does not know  -> "US"
 *     (imperial, first-last, US contact rules).
 *   - "JP" -> metric, last-first, JP contact rules.
 *   - A present field always wins over the built-in default; an absent field
 *     leaves the built-in value untouched. Units are display-only: storage and
 *     the API stay cm/kg in every market.
 */
import {
  getActiveMarket,
  getContactRules,
  getEnabledLocales,
  getNameOrder,
  resetActiveRegion,
  setActiveRegion,
  usesMetricMeasurementSystem,
} from "./regionalProfile";

// Where the resolved region is cached for the session, so a page reload (which
// remounts React before the users API is re-fetched) restores the same region
// instead of flashing US units for a JP account.
const ACTIVE_REGION_STORAGE_KEY = "activeRegion";

/**
 * Pull the raw region object out of whatever the caller has: the users-API
 * payload (`{ ...user, region }`), an already-unwrapped `{ data: { region } }`,
 * or a bare region object. Returns `{}` when there is nothing usable, so the
 * normalizer can still apply pure defaults.
 */
const extractRawRegion = (source) => {
  if (!source || typeof source !== "object") return {};
  if (source.region && typeof source.region === "object") return source.region;
  if (source.data?.region && typeof source.data.region === "object") {
    return source.data.region;
  }
  // A bare region object (has any of the region fields directly on it).
  if (
    "market" in source ||
    "measurement_system" in source ||
    "measurementSystem" in source
  ) {
    return source;
  }
  return {};
};

/**
 * Map a raw region (snake_case from the API, or camelCase if a caller already
 * normalized it) onto the profile shape `setActiveRegion` accepts. Unknown or
 * malformed values are dropped here and defaulted downstream, never thrown.
 */
export const normalizeRegion = (source) => {
  const raw = extractRawRegion(source);
  const pick = (...keys) => {
    for (const key of keys) {
      if (raw[key] != null && raw[key] !== "") return raw[key];
    }
    return undefined;
  };
  const market = pick("market", "country", "region_code", "regionCode");
  const measurementSystem = pick(
    "measurement_system",
    "measurementSystem",
    "unit_system",
    "unitSystem",
  );
  const nameOrder = pick("name_order", "nameOrder");
  const contactRules = pick("contact_rules", "contactRules");
  const defaultLocale = pick("default_locale", "defaultLocale");
  return {
    market: market == null ? undefined : String(market).trim().toUpperCase(),
    measurementSystem:
      measurementSystem == null
        ? undefined
        : String(measurementSystem).trim().toLowerCase(),
    nameOrder:
      nameOrder == null ? undefined : String(nameOrder).trim().toLowerCase(),
    contactRules:
      contactRules == null
        ? undefined
        : String(contactRules).trim().toUpperCase(),
    defaultLocale:
      defaultLocale == null
        ? undefined
        : String(defaultLocale).trim().toLowerCase(),
    allowedLocales: pick("allowed_locales", "allowedLocales"),
    enabledLocales: pick("enabled_locales", "enabledLocales"),
  };
};

const persist = (region) => {
  try {
    globalThis.sessionStorage?.setItem(
      ACTIVE_REGION_STORAGE_KEY,
      JSON.stringify(region),
    );
  } catch {
    // Storage may be unavailable (private mode, quota). The region is still
    // live in memory for this page; only cross-reload restore is lost.
  }
};

/**
 * Apply the region carried by a users-API payload as the active region, and
 * cache it for reload. Call this once after `fetchLoggedUserInfo` resolves.
 * Safe to call with a payload that has no region — it applies US defaults.
 * Returns the resolved profile.
 */
export const applyRegionFromUser = (userInfo) => {
  const region = normalizeRegion(userInfo);
  const resolved = setActiveRegion(region);
  persist(region);
  return resolved;
};

/**
 * Restore the region cached earlier this session. Call once at app start
 * (before the users API round-trips) so a reload keeps the account's region.
 * A no-op when nothing is cached; the app then stays on US until the users API
 * responds and `applyRegionFromUser` runs.
 */
export const hydrateActiveRegion = () => {
  try {
    const raw = globalThis.sessionStorage?.getItem(ACTIVE_REGION_STORAGE_KEY);
    if (!raw) return null;
    return setActiveRegion(JSON.parse(raw));
  } catch {
    return null;
  }
};

/** Clear both the in-memory and cached region when the account signs out. */
export const clearActiveRegion = () => {
  resetActiveRegion();
  try {
    globalThis.sessionStorage?.removeItem(ACTIVE_REGION_STORAGE_KEY);
  } catch {
    // The in-memory reset still prevents the previous account from leaking
    // into the next session when browser storage is unavailable.
  }
};

/* ----------------------------------------------------------- convenience API
 * Thin re-exports so screens can depend on this one utility for "what region
 * am I in right now" instead of reaching into regionalProfile's market-aware
 * resolvers with an undefined argument. All read the active region.
 */

/** Display units for the active region (metric = cm/kg). */
export const activeUsesMetric = () => usesMetricMeasurementSystem();

/** "first_last" | "last_first" for the active region. */
export const activeNameOrder = () => getNameOrder();

/** Postal/phone rule set object for the active region. */
export const activeContactRules = () => getContactRules();

/** Locales shippable in the active region today. */
export const activeEnabledLocales = () => getEnabledLocales();

/** Current market code (e.g. "US", "JP"). */
export const activeMarket = () => getActiveMarket();
